import { FavoritesTypes } from './types';
import api from '../../services/api';
import cacheService from '../../services/CacheService';
import { normalizeVideoData } from '../../utils/dataHelpers';

// Cache key used by api.js interceptor for favorites GET requests
const FAVORITES_CACHE_KEY = '/api/user/favorites{}';

// Toast notification function
const showToastNotification = (message, status = 'success') => {
  return {
    title: message,
    status: status,
    duration: 3000,
    isClosable: true,
    position: 'top',
  };
};

// Fetch user's favorites
export const fetchFavorites = () => async (dispatch) => {
  dispatch({ type: FavoritesTypes.FETCH_FAVORITES_REQUEST });
  
  try {
    console.log('[FAVORITES-REDUX] Fetching user favorites');
    const response = await api.get('/api/user/favorites');
    
    dispatch({
      type: FavoritesTypes.FETCH_FAVORITES_SUCCESS,
      payload: response.data.favorites
    });
    
    return response.data.favorites;
  } catch (error) {
    console.error('[FAVORITES-ERROR] Error fetching favorites:', error);
    
    dispatch({
      type: FavoritesTypes.FETCH_FAVORITES_FAILURE,
      payload: error.response?.data?.message || error.message || 'Failed to fetch favorites'
    });
  }
};

// Add video to favorites
export const addToFavorites = (videoData) => async (dispatch) => {
  dispatch({ type: FavoritesTypes.ADD_TO_FAVORITES_REQUEST });
  
  try {    console.log('[FAVORITES-REDUX] Adding to favorites:', videoData);
    
    // API call to add to favorites
    const response = await api.post('/api/user/favorites/videos', videoData);
    
    // Invalidate cached favorites so next fetch gets fresh data
    cacheService.remove(FAVORITES_CACHE_KEY);
    
    dispatch({
      type: FavoritesTypes.ADD_TO_FAVORITES_SUCCESS,
      payload: response.data.favorites
    });
    
    // Show success toast
    dispatch({
      type: FavoritesTypes.SHOW_TOAST,
      payload: showToastNotification('Added to Favorites', 'success')
    });
    
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[FAVORITES-ERROR] Error adding to favorites:', error);
    
    dispatch({
      type: FavoritesTypes.ADD_TO_FAVORITES_FAILURE,
      payload: error.response?.data?.message || error.message || 'Failed to add video to favorites'
    });
    
    // Show error toast
    dispatch({
      type: FavoritesTypes.SHOW_TOAST,
      payload: showToastNotification('Failed to add to favorites', 'error')
    });
    
    throw error; // Re-throw to allow handling in the component
  }
};

// Remove video from favorites
export const removeFromFavorites = (videoId) => async (dispatch) => {
  dispatch({
    type: FavoritesTypes.REMOVE_FROM_FAVORITES_REQUEST,
    payload: videoId
  });
  
  try {
    console.log(`[FAVORITES-REDUX] Removing video ${videoId} from favorites`);
    const response = await api.delete(`/api/user/favorites/videos/${videoId}`);
    
    // Invalidate cached favorites so next fetch gets fresh data
    cacheService.remove(FAVORITES_CACHE_KEY);
    
    dispatch({
      type: FavoritesTypes.REMOVE_FROM_FAVORITES_SUCCESS,
      payload: response.data.favorites
    });
    
    // Show success toast
    dispatch({
      type: FavoritesTypes.SHOW_TOAST,
      payload: showToastNotification('Removed from Favorites', 'success')
    });
    
    return { success: true, favorites: response.data.favorites };
  } catch (error) {
    console.error('[FAVORITES-ERROR] Error removing from favorites:', error);
    
    dispatch({
      type: FavoritesTypes.REMOVE_FROM_FAVORITES_FAILURE,
      payload: error.response?.data?.message || error.message || 'Failed to remove video from favorites'
    });
    
    // Show error toast
    dispatch({
      type: FavoritesTypes.SHOW_TOAST,
      payload: showToastNotification('Failed to remove from favorites', 'error')
    });
    
    return { success: false, error: error.response?.data?.message || error.message };
  }
};

// Reorder videos in favorites
export const reorderFavorites = (videoPositions) => async (dispatch) => {
  dispatch({
    type: FavoritesTypes.REORDER_FAVORITES_REQUEST,
    payload: videoPositions
  });
  
  try {
    console.log('[FAVORITES-REDUX] Reordering favorites');
    const response = await api.put('/api/user/favorites/reorder', { videoPositions });
    
    // Invalidate cached favorites so next fetch gets fresh data
    cacheService.remove(FAVORITES_CACHE_KEY);
    
    dispatch({
      type: FavoritesTypes.REORDER_FAVORITES_SUCCESS,
      payload: response.data.favorites
    });
    
    return { success: true, favorites: response.data.favorites };
  } catch (error) {
    console.error('[FAVORITES-ERROR] Error reordering favorites:', error);
    
    dispatch({
      type: FavoritesTypes.REORDER_FAVORITES_FAILURE,
      payload: error.response?.data?.message || error.message || 'Failed to reorder favorites'
    });
    
    return { success: false, error: error.response?.data?.message || error.message };
  }
};

// Load favorites for playback - similar to loadQueueForPlayback
export const loadFavoritesForPlayback = () => async (dispatch) => {
  dispatch({ type: FavoritesTypes.LOAD_FAVORITES_FOR_PLAYBACK_REQUEST });
  
  try {
    console.log('[FAVORITES-PLAYER] Initializing favorites playback');
    console.time('favorites-player-load');
    
    // Fetch favorites with full video data
    const response = await api.get('/api/user/favorites');
    const { videos: rawVideos } = response.data.favorites;
    
    // Normalize all videos to ensure consistent properties
    const videos = rawVideos.map(video => normalizeVideoData(video))
                           .sort((a, b) => a.position - b.position);
    
    console.timeEnd('favorites-player-load');
    console.log(`[FAVORITES-PLAYER] Loaded favorites with ${videos.length} videos`);
    
    dispatch({ 
      type: FavoritesTypes.LOAD_FAVORITES_FOR_PLAYBACK_SUCCESS, 
      payload: {
        videos,
        startIndex: 0
      }
    });
    
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[FAVORITES-PLAYER] Failed to load favorites for playback:', error);
    
    dispatch({ 
      type: FavoritesTypes.LOAD_FAVORITES_FOR_PLAYBACK_FAILURE, 
      payload: error.response?.data?.message || 'Failed to load favorites for playback' 
    });
    
    return { success: false, error: error.message };
  }
};

// Set playback index
export const setFavoritesPlaybackIndex = (index) => ({
  type: FavoritesTypes.SET_FAVORITES_PLAYBACK_INDEX,
  payload: index
});

// Navigation controls for player
export const nextFavoritesVideo = () => ({
  type: FavoritesTypes.NEXT_FAVORITES_VIDEO
});

export const previousFavoritesVideo = () => ({
  type: FavoritesTypes.PREVIOUS_FAVORITES_VIDEO
});

// Toggle autoplay
export const toggleFavoritesAutoplay = (enabled) => ({
  type: FavoritesTypes.TOGGLE_FAVORITES_AUTOPLAY,
  payload: enabled
});

// Clear playback state
export const clearFavoritesPlayback = () => ({
  type: FavoritesTypes.CLEAR_FAVORITES_PLAYBACK
});
